import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { baggageFeesOptions } from "@jb-dotcom/api/bag-calculator";
import { transformedDataOptions } from "@jb-dotcom/api/odb/transformed";
import { useSelect } from "@jetblue/kit/input-select";
import { useQuery } from "@tanstack/react-query";
import type { Key } from "react";
import { useCallback, useMemo, useState } from "react";
import { processBagFees } from "./helper/process-bag-fees";

export type UseBagCalculatorProperties = {
  /**
   * API URL for bag calculator endpoint.
   */
  apiUrl: string;
  /**
   * Base URL for OD query API requests.
   */
  baseUrl?: string | undefined;
  /**
   * Available fare class options for the selector.
   */
  fareClassOptions: { label: string; value: string }[];
  /**
   * Label to use for included bags.
   */
  includedBagLabel: string;
  /**
   * Initial date for the bag calculator. Defaults to today.
   */
  initialDate?: CalendarDate | undefined;
  /**
   * Initial destination airport code.
   */
  initialDestination?: string | undefined;
  /**
   * Initial fare class selection. Defaults to first available option.
   */
  initialFareClass?: string | undefined;
  /**
   * Initial origin airport code.
   */
  initialOrigin?: string | undefined;
  /**
   * Query origin for OD data.
   */
  queryOrigin?: string | undefined;
  /**
   * Subscription key for bag calculator API.
   */
  subscriptionKey: string;
};
export const useBagCalculator = ({
  apiUrl,
  baseUrl = "",
  fareClassOptions,
  includedBagLabel,
  initialDate = today(getLocalTimeZone()),
  initialFareClass = "",
  initialOrigin = "",
  initialDestination = "",
  queryOrigin = "nyc",
  subscriptionKey,
}: UseBagCalculatorProperties) => {
  const [selectedDate, setSelectedDate] = useState<CalendarDate | undefined>(
    initialDate,
  );
  const [origin, setOrigin] = useState(initialOrigin);
  const [destination, setDestination] = useState(initialDestination);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const fareClassSelect = useSelect<string>({
    initialSelectedKey: initialFareClass || (fareClassOptions[0]?.value ?? ""),
  });

  const handleDateChange = useCallback((date: CalendarDate | undefined) => {
    setSelectedDate(date);
    setIsSubmitted(false);
    setShowValidation(false);
  }, []);

  const handleOriginChange = useCallback((value: string) => {
    setOrigin(value);
    setIsSubmitted(false);
    setShowValidation(false);
  }, []);

  const handleDestinationChange = useCallback((value: string) => {
    setDestination(value);
    setIsSubmitted(false);
    setShowValidation(false);
  }, []);

  const handleFareClassChange = useCallback(
    (key: Key | null) => {
      fareClassSelect.onSelectionChange(key);
      setIsSubmitted(false);
      setShowValidation(false);
    },
    [fareClassSelect],
  );

  const dateForRequest = selectedDate ?? initialDate;
  const query = useQuery({
    ...baggageFeesOptions(
      {
        departureDate: new CalendarDate(
          dateForRequest.year,
          dateForRequest.month,
          dateForRequest.day,
        ).toString(),
        destination,
        fareClass: fareClassSelect.value,
        origin,
      },
      apiUrl,
      subscriptionKey,
    ),
    enabled:
      isSubmitted &&
      Boolean(origin && destination && selectedDate && fareClassSelect.value),
    select: (data) => processBagFees(data, includedBagLabel),
  });

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const isValid = Boolean(
        origin && destination && selectedDate && fareClassSelect.value,
      );
      if (!isValid) {
        setShowValidation(true);
        return;
      }
      setIsSubmitted(true);
    },
    [origin, destination, selectedDate, fareClassSelect.value],
  );

  const odQuery = useQuery(transformedDataOptions(queryOrigin, baseUrl));

  const unitedStatesOdData = useMemo(
    () => (odQuery.data ? odQuery.data.filter((airport) => airport.jb) : []),
    [odQuery.data],
  );

  return {
    destination,
    fareClassSelect,
    handleFareClassChange,
    handleSubmit,
    origin,
    query,
    selectedDate,
    setDestination: handleDestinationChange,
    setOrigin: handleOriginChange,
    setSelectedDate: handleDateChange,
    showValidation,
    usOdData: unitedStatesOdData,
  };
};
